import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  extractUpdatableSections,
  removeElementAttributeBySelector,
  removeUpdatableAttribute,
} from '@/entities/section-picker';
import { notificationService } from '@/lib/services/notification.service';
import { SELECTION_HELPER_ATTR } from './applyElementConfig';

export type TemplateViewMode = 'code' | 'preview';

interface UseTemplateEditorStateParams {
  /** Server-side HTML for the selected template. */
  savedHtml: string;
}

export function useTemplateEditorState({ savedHtml }: UseTemplateEditorStateParams) {
  const { t } = useTranslation('admin');

  const [html, setHtml] = useState(savedHtml);
  const [isDirty, setIsDirty] = useState(false);
  // A template with markup opens on the rendered result; an empty one opens on the editor, because
  // there is nothing to preview and pasting HTML is the only sensible next action.
  const [viewMode, setViewMode] = useState<TemplateViewMode>(() =>
    savedHtml.trim().length > 0 ? 'preview' : 'code',
  );
  const [mobilePreview, setMobilePreview] = useState(false);

  // Adopt server updates only while the draft is clean — after a save, or when another session
  // changed this template. A dirty draft is the user's unsaved work and must never be overwritten.
  useEffect(() => {
    if (!isDirty) setHtml(savedHtml);
  }, [savedHtml, isDirty]);

  const sections = useMemo(() => extractUpdatableSections(html), [html]);

  const updateHtml = useCallback((next: string) => {
    setHtml(next);
    setIsDirty(true);
  }, []);

  const removeSection = useCallback(
    (sectionName: string) => {
      setHtml((current) => {
        // The helper attribute is addressed VIA the updatable attribute, so it has to go first —
        // stripping the tag first would leave an orphaned `data-selection-helper` behind. Targeted
        // by selector so other images keep theirs.
        const withoutHelper = removeElementAttributeBySelector(
          current,
          `[data-updatable-${sectionName}]`,
          SELECTION_HELPER_ATTR,
        );
        return removeUpdatableAttribute(withoutHelper, sectionName);
      });
      setIsDirty(true);
      notificationService.showSuccess(
        t('templatesConfig.editableContents.sectionRemoved', { sectionName }),
      );
    },
    [t],
  );

  // A ref so the check below sees the current draft without `markSaved` changing identity on every
  // keystroke. Safe to write during render: it is only ever read from an event callback.
  const htmlRef = useRef(html);
  htmlRef.current = html;

  /**
   * Report that `savedSnapshot` reached the server. Nothing disables the editor during a save, so the
   * draft may have moved past what was sent — clearing the flag then hands it to the adopt-on-clean
   * effect above, which replaces the newer work with the older saved value.
   */
  const markSaved = useCallback((savedSnapshot: string) => {
    if (htmlRef.current === savedSnapshot) setIsDirty(false);
  }, []);

  return {
    html,
    isDirty,
    sections,
    viewMode,
    setViewMode,
    mobilePreview,
    setMobilePreview,
    updateHtml,
    removeSection,
    markSaved,
  };
}
